/**
 * Authentication and Authorization Helper Functions
 */

/**
 * Check if user has completed setup (has at least one role)
 */
export const hasCompletedSetup = (userRoles) => {
  return userRoles && userRoles.length > 0;
};

/**
 * Get user's primary role (highest in hierarchy)
 */
export const getPrimaryRole = (userRoles) => {
  if (!userRoles || userRoles.length === 0) return null;

  const roleOrder = ['system_admin', 'club_admin', 'team_captain', 'player'];

  for (const role of roleOrder) {
    const found = userRoles.find((r) => r.role === role);
    if (found) return found;
  }

  return userRoles[0];
};

/**
 * Get default redirect path based on user's role
 */
export const getDefaultRedirectPath = (userRoles) => {
  const primaryRole = getPrimaryRole(userRoles);

  if (!primaryRole) {
    return '/setup'; // No roles, needs setup
  }

  switch (primaryRole.role) {
    case 'system_admin':
      return '/admin';
    case 'club_admin':
      return `/club/${primaryRole.club_id}`;
    case 'team_captain':
      return `/team/${primaryRole.team_id}/captain`;
    case 'player':
      return `/team/${primaryRole.team_id}/player`;
    default:
      return '/';
  }
};

/**
 * Format user display name from auth user
 */
export const getUserDisplayName = (user) => {
  if (!user) return 'Guest';

  // Try user metadata first
  if (user.user_metadata?.full_name) {
    return user.user_metadata.full_name;
  }
  if (user.user_metadata?.name) {
    return user.user_metadata.name;
  }

  // Fall back to email
  if (user.email) {
    return user.email.split('@')[0];
  }

  return 'User';
};

/**
 * Get user avatar URL
 */
export const getUserAvatar = (user) => {
  if (!user) return null;

  // Google OAuth provides avatar_url
  if (user.user_metadata?.avatar_url) {
    return user.user_metadata.avatar_url;
  }
  if (user.user_metadata?.picture) {
    return user.user_metadata.picture;
  }

  return null;
};

/**
 * Format role for display
 */
export const formatRole = (role) => {
  const roleMap = {
    system_admin: 'System Administrator',
    club_admin: 'Club Administrator',
    team_captain: 'Team Captain',
    player: 'Player',
  };
  return roleMap[role] || role;
};

/**
 * Get role badge color (for Tailwind classes)
 */
export const getRoleBadgeColor = (role) => {
  const colorMap = {
    system_admin: 'bg-purple-100 text-purple-800',
    club_admin: 'bg-blue-100 text-blue-800',
    team_captain: 'bg-green-100 text-green-800',
    player: 'bg-gray-100 text-gray-800',
  };
  return colorMap[role] || 'bg-gray-100 text-gray-800';
};

/**
 * Check if invitation token is valid
 */
export const isInvitationValid = (invitation) => {
  if (!invitation) return false;
  if (invitation.status !== 'pending') return false;

  const expiresAt = new Date(invitation.expires_at);
  const now = new Date();

  return expiresAt > now;
};

/**
 * Generate invitation URL
 */
export const getInvitationUrl = (token) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/invite/${token}`;
};

/**
 * Parse JWT token (client-side, for debugging only - don't trust for auth!)
 */
export const parseJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

/**
 * Check if user session is about to expire (within 5 minutes)
 */
export const isSessionExpiringSoon = (session) => {
  if (!session) return false;

  const expiresAt = session.expires_at || session.exp;
  if (!expiresAt) return false;

  const expiryTime = typeof expiresAt === 'number'
    ? expiresAt * 1000 // Convert seconds to milliseconds
    : new Date(expiresAt).getTime();

  const fiveMinutes = 5 * 60 * 1000;
  const now = Date.now();

  return expiryTime - now < fiveMinutes;
};

/**
 * Refresh session if needed
 */
export const refreshSessionIfNeeded = async (supabase, session) => {
  if (isSessionExpiringSoon(session)) {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      console.error('Error refreshing session:', error);
      return null;
    }
    return data.session;
  }
  return session;
};
